import { useEffect, useRef, useState } from 'react';
import './style.scss';
import { DateTime } from 'luxon';
import flatten from 'lodash/flatten';
import orderBy from 'lodash/orderBy';
import union from 'lodash/union';
import { TranslationService } from '../../../services/translation.service';
import { SanitizerService } from '../../../services/sanitizer.service';
import type { ITrafficSituation } from '../../../models/traffic-situation.model';
import ClearButtonComponent from '../../Shared/ClearButton';
import TrafficSituationApiModel from '../models/traffic-situation.api-model';
import { TransportMode } from '../../../models/transport-mode.enum';
import { TrafficSituationHelper } from '../../../helpers/traffic-situation.helper';
import TrafficSituation from './components/TrafficSituation';
import TrafficSituationV2 from './components/TrafficSituationV2';
import { AnalyticsService } from '../../../services/analytics-service';
import TransportModeFilter from '../../Shared/TransportModeFilter';
import ValidationInfo from '../../../validation/validation-info';
import { TransportModeHelper } from '../../../helpers/transport-mode.helper';
import { QueryStringHelper } from '../../../helpers/query-string.helper';
import useFetch, { Status } from '../../../hooks/fetch.hook';
import RadioFormGroup from '../../Shared/RadioFormGroup';
import CheckboxFormGroup from '../../Shared/CheckboxFormGroup';
import { TrafficInformationFeedbackService } from '../../../services/traffic-information-feedback.service';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import MunicipalityApiModel from '../models/municipality.api-model';
import { LineHelper } from '../../../helpers/line.helper';
import { IMultiFacetSelector } from '../../Shared/MultiFacetSelector';
import MultiFacetSelector from '../../Shared/MultiFacetSelector';
import IMultiFacet from '../../../models/multi-facet.model';
import { MultiFacetType } from '../../../models/multi-facet-type.enum';

enum TrafficSituationType {
  current = 'current',
  planned = 'planned',
}

enum TrafficSituationSeverity {
  severe = 'severe',
  normal = 'normal',
  slight = 'slight',
}

enum SortOrder {
  latest = 'latest',
  severity = 'severity',
}

function processReceivedData(data: TrafficSituationApiModel[]): ITrafficSituation[] {
  return data?.map((ts) => TrafficSituationApiModel.mapToBusinessModel(ts)) ?? [];
}

function filterBasedOnMultiFacetItems(ts: ITrafficSituation, selectedMultiFacetItems: IMultiFacet[]): boolean {
  if (!selectedMultiFacetItems || selectedMultiFacetItems.length === 0) return true; // No filter applied if no value is selected

  // Check if any selected item matches the traffic situation
  return selectedMultiFacetItems.some((item) => {
    if (item.type === MultiFacetType.Line && item.line) {
      const line = item.line;
      // Check if the traffic situation affects the selected line
      return TrafficSituationHelper.getDistinctLines(ts).some((l) => l.gid === line.gid);
    }

    if (item.type === MultiFacetType.Municipality && item.municipality) {
      const municipality = item.municipality;
      // Check if the traffic situation affects the selected municipality
      const allAffectedMunicipalities = union(
        flatten(TrafficSituationHelper.getDistinctLines(ts).map((x) => x.municipalities ?? [])),
        ts.affectedStopPoints.filter((sp) => sp.municipality).map((sp) => sp.municipality!)
      );

      return allAffectedMunicipalities.some((m) => m.gid === municipality.gid);
    }

    return false;
  });
}

function filterBasedOnTransportMode(ts: ITrafficSituation, selectedTransportModes: TransportMode[]): boolean {
  if (ts.affectedLines.length === 0 && ts.affectedJourneys.length === 0) return true;
  return selectedTransportModes.some((tm) => TrafficSituationHelper.affectsLineWithTransportMode(ts, tm));
}

function filterBasedOnSeverity(ts: ITrafficSituation, selectedSeverities: TrafficSituationSeverity[]): boolean {
  return selectedSeverities.includes(ts.severity as TrafficSituationSeverity);
}

function filterBasedOnSituationType(ts: ITrafficSituation, selectedTrafficSituationType: TrafficSituationType[]): boolean {
  const today = DateTime.now().startOf('day');
  if (selectedTrafficSituationType.includes(TrafficSituationType.current) && ts.startTime.startOf('day') <= today && (!ts.endTime || ts.endTime.startOf('day') >= today)) return true;
  if (selectedTrafficSituationType.includes(TrafficSituationType.planned) && ts.startTime.startOf('day') > today) return true;
  return false;
}

function toSeveritySortOrder(severity: TrafficSituationSeverity): number {
  switch (severity) {
    case TrafficSituationSeverity.slight:
      return 1;
    case TrafficSituationSeverity.normal:
      return 2;
    case TrafficSituationSeverity.severe:
      return 3;
    default:
      return 0;
  }
}

function TrafficInformationSearch() {
  // === CONSTANTS ===
  const allTransportModes = [TransportMode.tram, TransportMode.bus, TransportMode.train, TransportMode.ferry];

  // === STATE ===
  const [selectedTrafficSituationType, setSelectedTrafficSituationType] = useState<TrafficSituationType[]>([TrafficSituationType.current, TrafficSituationType.planned] as TrafficSituationType[]);
  const [selectedSeverities, setSelectedSeverities] = useState<TrafficSituationSeverity[]>([
    TrafficSituationSeverity.slight,
    TrafficSituationSeverity.normal,
    TrafficSituationSeverity.severe,
  ] as TrafficSituationSeverity[]);
  const [selectedSortOrder, setSelectedSortOrder] = useState<SortOrder>(SortOrder.latest);
  const [selectedTransportModes, setSelectedTransportModes] = useState<TransportMode[]>(allTransportModes);
  const [selectedMultiFacetItems, setSelectedMultiFacetItems] = useState<IMultiFacet[]>([]);
  const [{ status: getTrafficSituationsStatus, data: getTrafficSituationsData }] = useFetch<TrafficSituationApiModel[]>('/api/trafficsituations/all');

  // === REFS ===
  const multiFacetSelectorRef = useRef<IMultiFacetSelector>(null);

  // === VARIABLES ===
  const commonSearches = [
    { type: MultiFacetType.Municipality, municipality: { gid: '9081014110001480', name: 'Göteborg', shortName: 'Göteborg' }, line: undefined },
    { type: MultiFacetType.Municipality, municipality: { gid: '9081014110001490', name: 'Borås', shortName: 'Borås' }, line: undefined },
    { type: MultiFacetType.Municipality, municipality: { gid: '9081014110001481', name: 'Mölndal', shortName: 'Mölndal' }, line: undefined },
    { type: MultiFacetType.Municipality, municipality: { gid: '9081014110001488', name: 'Trollhättan', shortName: 'Trollhättan' }, line: undefined },
    { type: MultiFacetType.Municipality, municipality: { gid: '9081014110001496', name: 'Skövde', shortName: 'Skövde' }, line: undefined },
    { type: MultiFacetType.Municipality, municipality: { gid: '9081014110001485', name: 'Uddevalla', shortName: 'Uddevalla' }, line: undefined },
  ];

  const allTrafficSituations: ITrafficSituation[] =
    getTrafficSituationsStatus !== Status.success || !getTrafficSituationsData
      ? []
      : orderBy(
          processReceivedData(getTrafficSituationsData)
            .filter((ts) => filterBasedOnSituationType(ts, selectedTrafficSituationType))
            .filter((ts) => filterBasedOnTransportMode(ts, selectedTransportModes))
            .filter((ts) => filterBasedOnMultiFacetItems(ts, selectedMultiFacetItems))
            .filter((ts) => filterBasedOnSeverity(ts, selectedSeverities)),
          selectedSortOrder === SortOrder.latest
            ? [(ts) => (TrafficSituationHelper.isNewTrafficSituation(ts) ? 2 : TrafficSituationHelper.isCurrentTrafficSituation(ts) ? 1 : 0), (ts) => ts.startTime]
            : [(ts) => toSeveritySortOrder(ts.severity as TrafficSituationSeverity)],
          'desc'
        );

  // === EFFECTS ===
  useEffect(() => {
    async function setMunicipalitiesFromQuerystring() {
      const municipalityIds = QueryStringHelper.getValuesFromQueryString('k');
      if (municipalityIds?.length > 0) {
        try {
          const response = await fetch('/api/trafficsituations/municipalities');
          if (!response.ok) throw new Error(response.statusText);

          const municipalities = ((await response.json()) as MunicipalityApiModel[]).map((m) => MunicipalityApiModel.mapToBusinessModel(m));

          const filteredMunicipalities = municipalities.filter((m) => municipalityIds.includes(m.gid));

          setSelectedMultiFacetItems(
            filteredMunicipalities.map((m) => ({
              type: MultiFacetType.Municipality,
              municipality: m,
              line: undefined,
            }))
          );
        } catch {
          /* empty */
        }
      }
    }

    function setTransportModesFromQuerystring() {
      try {
        const transportModes = QueryStringHelper.getValuesFromQueryString('t');
        if (transportModes?.length > 0) {
          setSelectedTransportModes(transportModes.map((transportMode) => TransportModeHelper.parse(transportMode)));
        }
      } catch {
        /* empty */
      }
    }

    function setTrafficSituationTypeFromQuerystring() {
      try {
        const trafficSituationTypes = QueryStringHelper.getValuesFromQueryString('g');
        if (trafficSituationTypes?.length > 0) {
          setSelectedTrafficSituationType(trafficSituationTypes.map((type) => type as TrafficSituationType));
        }
      } catch {
        /* empty */
      }
    }

    setMunicipalitiesFromQuerystring();
    setTransportModesFromQuerystring();
    setTrafficSituationTypeFromQuerystring();
  }, []);

  // ======================================================================
  // Event handlers
  // ======================================================================
  function handleMultiFacetSelect(multiFacetItems: IMultiFacet[]): void {
    if (multiFacetItems.length > 0) {
      setSelectedMultiFacetItems(multiFacetItems);
    } else {
      setSelectedMultiFacetItems([]);
    }

    // Group items by type
    const lines = multiFacetItems
      .filter((item) => item.type === MultiFacetType.Line && item.line)
      .map((item) => {
        const line = item.line!;
        return `${LineHelper.getLineNameAndDirection(line)}, ${line.gid}`;
      });

    const municipalities = multiFacetItems
      .filter((item) => item.type === MultiFacetType.Municipality && item.municipality)
      .map((item) => {
        const municipality = item.municipality!;
        return `${municipality.name}, ${municipality.gid}`;
      });

    // Send a single analytics event for lines
    if (lines.length > 0) {
      AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Sökalternativ - Linjer', lines.join('; '));
    }

    // Send a single analytics event for municipalities
    if (municipalities.length > 0) {
      AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Sökalternativ - Kommuner', municipalities.join('; '));
    }
  }

  function handleTransportModeFilterChange(transportModes: TransportMode[]): void {
    setSelectedTransportModes(transportModes);
    AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Sökalternativ - Trafikslag', transportModes.map((transportMode: TransportMode) => transportMode.toString()).toString());
    TrafficInformationFeedbackService.registerSelectedTransportModes(selectedTransportModes, transportModes);
  }

  function handleClearClick(): void {
    setSelectedTransportModes(allTransportModes);
    multiFacetSelectorRef.current!.clear();
    setSelectedMultiFacetItems([]);
    setSelectedTrafficSituationType([TrafficSituationType.current, TrafficSituationType.planned]);
    setSelectedSeverities([TrafficSituationSeverity.slight, TrafficSituationSeverity.normal, TrafficSituationSeverity.severe]);
    setSelectedSortOrder(SortOrder.latest);
    AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Klick på återställ', '');
    TrafficInformationFeedbackService.registerSelectedTransportModes(selectedTransportModes, allTransportModes);
  }

  return (
    <div className="traffic-information-search">
      <div className="traffic-information-search__filters shadow-sm rounded p-sm-3 mb-4">
        <div className="mb-3">
          {TranslationService.get('common.filterupdatemessage') && <div className="bg-gray p-3 mb-3">{TranslationService.get('common.filterupdatemessage')}</div>}
          <MultiFacetSelector
            id="multi-facet-selector"
            ref={multiFacetSelectorRef}
            labelText={TranslationService.get('trafficsituationspage.searchlinelabel')}
            placeholderText={TranslationService.get('trafficsituationspage.searchlineplaceholdertext')}
            noResultsText={TranslationService.get('trafficsituationspage.searchlinenoresultstext') || TranslationService.get('common.autocomplete.noresults.default')}
            onChange={handleMultiFacetSelect}
            selectedMultiFacetItems={selectedMultiFacetItems}
            commonSearches={commonSearches}
          />
        </div>

        <fieldset className="mb-3">
          <legend>{TranslationService.get('trafficsituationspage.transportmodelabel')}</legend>
          <TransportModeFilter
            availableTransportModes={[TransportMode.tram, TransportMode.bus, TransportMode.train, TransportMode.ferry]}
            selectedTransportModes={selectedTransportModes}
            onChange={handleTransportModeFilterChange}
          ></TransportModeFilter>
        </fieldset>

        <CheckboxFormGroup
          id="traffic-situation-type"
          labelText={TranslationService.get('trafficsituationspage.validityperiod')}
          rowLayoutMdUp={true}
          options={[
            { value: TrafficSituationType.current, text: TranslationService.get('trafficsituationspage.currenttrafficsituations') },
            { value: TrafficSituationType.planned, text: TranslationService.get('trafficsituationspage.upcomingtrafficsituations') },
          ]}
          state={{ value: selectedTrafficSituationType, validationInfo: new ValidationInfo() }}
          onChange={(value: string[]) => setSelectedTrafficSituationType(value as TrafficSituationType[])}
        ></CheckboxFormGroup>

        <RadioFormGroup
          id="sort-order"
          labelText={TranslationService.get('trafficsituationspage.sortorderlabel')}
          rowLayout={true}
          rowLayoutSmUp={true}
          options={[
            { value: SortOrder.latest, text: TranslationService.get('trafficsituationspage.sortorder.latest') },
            { value: SortOrder.severity, text: TranslationService.get('trafficsituationspage.sortorder.severity') },
          ]}
          state={{ value: selectedSortOrder, validationInfo: new ValidationInfo() }}
          onChange={(value: string) => setSelectedSortOrder(value as SortOrder)}
        ></RadioFormGroup>
        <div>
          <ClearButtonComponent onClick={handleClearClick}></ClearButtonComponent>
        </div>
      </div>

      {getTrafficSituationsStatus === Status.loading && (
        <div className="mb-4 pt-3">
          <div className="spinner-container">
            <div className="spinner"></div>
          </div>
        </div>
      )}

      {getTrafficSituationsStatus !== Status.loading && (
        <div>
          <div>
            <h2 aria-live="polite">
              <span>
                {allTrafficSituations.length === 0 ? (
                  <>
                    <span className="visually-hidden">{allTrafficSituations.length}</span> {TranslationService.get('trafficsituationspage.trafficsituations')}
                  </>
                ) : (
                  `${allTrafficSituations.length} ${TranslationService.get(
                    allTrafficSituations.length === 1 ? 'trafficsituationspage.trafficsituation' : 'trafficsituationspage.trafficsituations'
                  ).toLowerCase()}`
                )}
              </span>
            </h2>
            {allTrafficSituations.length > 0 &&
              allTrafficSituations.map((ts, index) => (
                <div key={`${ts.situationNumber}${index}`} className={`${index < allTrafficSituations.length - 1 ? 'mb-3' : ''} ${index === 0 ? 'mt-3' : ''}`}>
                  {FeatureFlagService.enableTrafficInformationMessagesVersionTwo ? <TrafficSituationV2 trafficSituation={ts} /> : <TrafficSituation trafficSituation={ts} />}
                </div>
              ))}
            {!allTrafficSituations.length && (
              <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('trafficsituationspage.notrafficsituationsfoundtext')) }}></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TrafficInformationSearch;
